import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { apiFetch } from '../lib/api';
import type { WaSessionInfo } from '../types';

/**
 * WhatsApp pairing flow. After the user clicks "Connect WhatsApp" on the
 * sidebar, we POST /wa/pair to spawn the worker, then poll /wa/status
 * every 2s for the QR or terminal state.
 */
export function WaPairModal({
  firmId,
  onPaired,
  onClose,
}: {
  firmId: string;
  onPaired: (phone: string) => void;
  onClose: () => void;
}) {
  const { t } = useTranslation(['wa', 'common']);
  const [info, setInfo] = useState<WaSessionInfo | null>(null);
  const [error, setError] = useState<string | null>(null);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    let cancelled = false;

    async function start() {
      try {
        await apiFetch(`/api/firms/${firmId}/wa/pair`, { method: 'POST' });
      } catch (err) {
        if (cancelled) return;
        setError(err instanceof Error ? err.message : String(err));
        return;
      }

      pollRef.current = setInterval(async () => {
        try {
          const next = await apiFetch<WaSessionInfo>(`/api/firms/${firmId}/wa/status`);
          if (cancelled) return;
          setInfo(next);
          if (next.status === 'connected' && next.pairedPhone) {
            if (pollRef.current) clearInterval(pollRef.current);
            onPaired(next.pairedPhone);
          } else if (next.status === 'banned') {
            if (pollRef.current) clearInterval(pollRef.current);
            setError(t('status.banned'));
          }
        } catch (err) {
          if (cancelled) return;
          setError(err instanceof Error ? err.message : String(err));
        }
      }, 2000);
    }

    void start();

    return () => {
      cancelled = true;
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, [firmId, onPaired, t]);

  const status = info?.status ?? 'spawning';

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="form-card modal-card" onClick={(e) => e.stopPropagation()}>
        <div className="form-card-header">
          <div style={{ flex: 1 }}>
            <div className="form-preview-name">{t('modal.title')}</div>
            <div className="form-preview-type">{t(`status.${status}`)}</div>
          </div>
          <button
            type="button"
            className="modal-close-btn"
            onClick={onClose}
            aria-label={t('common:actions.close')}
          >
            ×
          </button>
        </div>

        <div className="form-section">
          <p style={{ fontSize: 13, color: 'var(--text2)', lineHeight: 1.5 }}>
            {t('modal.instructions')}
          </p>
        </div>

        <div className="form-section" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', padding: 18 }}>
          {info?.qr ? (
            <img
              src={info.qr}
              alt="WhatsApp QR"
              /*
               * Render at the natural 280px width but let height auto so a
               * non-square data URI can't get vertically squashed. width
               * also responds to narrower viewports.
               */
              style={{
                width: '100%',
                maxWidth: 280,
                height: 'auto',
                aspectRatio: '1 / 1',
                border: '1px solid var(--border)',
                borderRadius: 8,
                display: 'block',
              }}
            />
          ) : status === 'connected' ? (
            <div className="status-ok">{t('modal.scanned')}</div>
          ) : (
            <div className="empty" style={{ padding: '40px 0' }}>{t('modal.waiting_for_qr')}</div>
          )}
        </div>

        <div className="form-section" style={{ background: 'var(--amber-bg)', borderInlineStart: '3px solid var(--amber)' }}>
          <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--amber)', marginBottom: 4 }}>
            ⚠ {t('warning.title')}
          </div>
          <div style={{ fontSize: 12, color: 'var(--text)' }}>{t('warning.body')}</div>
        </div>

        {error && (
          <div className="form-section">
            <div className="signin-error">{t('modal.error', { message: error })}</div>
          </div>
        )}

        <div className="form-footer">
          <button type="button" className="btn-cancel" onClick={onClose}>
            {t('modal.close')}
          </button>
        </div>
      </div>
    </div>
  );
}
